import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { TimeInput } from "@/components/ui/time-input";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Clock, Globe, Save, Sliders, Power, PowerOff, Loader, AlertTriangle, Camera, Info, Play } from "lucide-react";
import { CellularNetworkSection } from "./CellularNetworkSection";
import { Separator } from "@/components/ui/separator";
import { Slider } from "@/components/ui/slider";
import { ImageCountDisplay } from "@/components/devices/ImageCountDisplay";
import { localToUTC, utcToLocal } from "@/utils/timezoneUtils";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useIsMobile } from "@/hooks/use-mobile";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useDeviceEditPermissions } from "@/hooks/use-device-edit-permissions";
import { useSimpleRole } from "@/hooks/use-simple-role";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useOptimisticDeviceState } from "@/hooks/use-optimistic-device-state";
import { useDeviceChangeLog } from "@/hooks/use-device-change-log";
import { OperationalDaysCard } from "./OperationalDaysCard";
import { EventScheduleCard } from "./EventScheduleCard";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";
import { useDeviceImageCapture, areImagesStopped, parseSnapshot, DEFAULT_RESTORE } from "@/hooks/use-device-image-capture";
const TIMEZONES = [{
  value: "Europe/London",
  label: "Europe/London - UTC+0 / +1"
}, {
  value: "Europe/Paris",
  label: "Europe/Paris - UTC+1 / +2"
}, {
  value: "Asia/Dubai",
  label: "Asia/Dubai - UTC+4"
}, {
  value: "Australia/Perth",
  label: "Australia/Perth - UTC+8"
}, {
  value: "Australia/Sydney",
  label: "Australia/Sydney - UTC+10 / +11"
}, {
  value: "America/New_York",
  label: "America/New_York - UTC−5 / −4"
}, {
  value: "America/Chicago",
  label: "America/Chicago - UTC−6 / −5"
}, {
  value: "America/Phoenix",
  label: "America/Phoenix - UTC−7"
}, {
  value: "America/Los_Angeles",
  label: "America/Los_Angeles - UTC−8 / −7"
}];
const mapSliderToLevel = (value: number): number => {
  const mapping: {
    [key: number]: number;
  } = {
    1: 6,
    2: 4,
    3: 2,
    4: 1,
    5: 3,
    6: 5
  };
  return mapping[value] || 0;
};
const mapLevelToSlider = (level: number): number => {
  const mapping: {
    [key: number]: number;
  } = {
    [6]: 1,
    [4]: 2,
    [2]: 3,
    [1]: 4,
    [3]: 5,
    [5]: 6
  };
  return mapping[level] || 1;
};
const msToDisplayValue = (ms: number): string => {
  const minutes = ms / 60000;
  if (minutes < 60) {
    return `${minutes} min`;
  }
  return `${minutes / 60} hours`;
};
const sliderToMs = (value: number): number => {
  const mapping: {
    [key: number]: number;
  } = {
    1: 900000,
    2: 1800000,
    3: 3600000,
    4: 7200000,
    5: 10800000,
    6: 14400000,
    7: 18000000,
    8: 21600000
  };
  return mapping[value] || 3600000;
};
const msToSlider = (ms: number): number => {
  const mapping: {
    [key: number]: number;
  } = {
    900000: 1,
    1800000: 2,
    3600000: 3,
    7200000: 4,
    10800000: 5,
    14400000: 6,
    18000000: 7,
    21600000: 8
  };
  return mapping[ms] || 3;
};
interface DeviceConfigProps {
  device: any;
  deviceId: string;
  onUpdate: () => Promise<void>;
}
export const DeviceConfig = ({
  device,
  deviceId,
  onUpdate
}: DeviceConfigProps) => {
  const { logChange } = useDeviceChangeLog();
  const [isUpdating, setIsUpdating] = useState(false);
  const [showWarningDialog, setShowWarningDialog] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showCancelHibernateDialog, setShowCancelHibernateDialog] = useState(false);
  const [confirmationText, setConfirmationText] = useState("");
  const [showStopImagesDialog, setShowStopImagesDialog] = useState(false);
  const [showStartImagesDialog, setShowStartImagesDialog] = useState(false);
  const [pendingUpdate, setPendingUpdate] = useState<{
    field: string;
    value: string | number;
    imagesPerDay?: number;
  } | null>(null);

  // Handle potential undefined data with defaults
  const defaultCameraConfig = {
    scheduleStart: "0",
    scheduleEnd: "0",
    cameraSaturation: 0,
    cameraEV: 0,
    cameraContrast: 0,
    cameraBrightness: 0,
    cameraAbsoluteExposure: "0",
    cameraISOSensitivity: "0",
    withinScheduleSleepTime: 3600000,
    cameraSetAutoExposure: 1,
    cameraSetAutoISOSensitive: 1,
    onDemand: 0
  };

  // Debug log to check the raw data structure from the device object
  console.log("Device object:", device);
  console.log("Camera config from device:", device?.camera_config);

  // Process camera_config data from the device object
  // The data may be stored as a string that needs parsing, or as a direct object
  let processedCameraConfig;
  try {
    // Check if camera_config exists and try to parse it if it's a string
    if (device?.camera_config) {
      if (typeof device.camera_config === 'string') {
        try {
          processedCameraConfig = JSON.parse(device.camera_config);
        } catch (e) {
          console.error("Failed to parse camera_config string:", e);
          processedCameraConfig = defaultCameraConfig;
        }
      } else if (typeof device.camera_config === 'object') {
        // If it's already an object, use it directly
        processedCameraConfig = device.camera_config;
      } else {
        console.log("camera_config is neither string nor object, using defaults");
        processedCameraConfig = defaultCameraConfig;
      }
    } else if (device?.cameraConfig) {
      // Fallback to camelCase property if it exists
      processedCameraConfig = device.cameraConfig;
    } else {
      console.log("No camera config found in device object, using defaults");
      processedCameraConfig = defaultCameraConfig;
    }
  } catch (error) {
    console.error("Error processing camera config:", error);
    processedCameraConfig = defaultCameraConfig;
  }
  console.log("Processed camera config:", processedCameraConfig);

  // Now safely access the camera config with defaults
  const cameraConfig = processedCameraConfig || defaultCameraConfig;

  // Convert to proper boolean values to ensure correct comparisons
  const isHibernating = device.deviceHibernate === 1 || device.deviceHibernate === "1";
  // Source of truth: device_details.camera_config.forceHibernate (processed above as `cameraConfig`).
  // Also fall back to camelCase API shape for safety.
  const rawForceHibernate =
    cameraConfig?.forceHibernate ?? device.cameraConfig?.forceHibernate;
  const forceHibernate =
    rawForceHibernate === 1 || rawForceHibernate === "1";

  // Debug log to verify the values we're working with
  console.log("Device hibernation state:", {
    isHibernating,
    forceHibernate,
    deviceHibernate: device.deviceHibernate,
    cameraConfigForceHibernate: device.cameraConfig?.forceHibernate,
    typeOfForceHibernate: typeof device.cameraConfig?.forceHibernate
  });
  const [timezone, setTimezone] = useState(device?.timezone || "Europe/London");
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || "Europe/London";
  const [localScheduleStart, setLocalScheduleStart] = useState(0);
  const [localScheduleEnd, setLocalScheduleEnd] = useState(0);
  const [utcScheduleStart, setUtcScheduleStart] = useState(Number(cameraConfig.scheduleStart || 0));
  const [utcScheduleEnd, setUtcScheduleEnd] = useState(Number(cameraConfig.scheduleEnd || 0));
  const [cameraSaturation, setCameraSaturation] = useState(mapLevelToSlider(Number(cameraConfig.cameraSaturation) || 0));
  const [cameraEV, setCameraEV] = useState(mapLevelToSlider(Number(cameraConfig.cameraEV) || 0));
  const [cameraContrast, setCameraContrast] = useState(mapLevelToSlider(Number(cameraConfig.cameraContrast) || 0));
  const [cameraBrightness, setCameraBrightness] = useState(mapLevelToSlider(Number(cameraConfig.cameraBrightness) || 0));
  const [absoluteExposure, setAbsoluteExposure] = useState(cameraConfig.cameraAbsoluteExposure || "0");
  const [isoSensitivity, setIsoSensitivity] = useState(cameraConfig.cameraISOSensitivity || "0");
  const [intervalTimer, setIntervalTimer] = useState(msToSlider(Number(cameraConfig.withinScheduleSleepTime) || 3600000));
  const [onDemandPhoto, setOnDemandPhoto] = useState(Boolean(Number(cameraConfig.onDemand) || 0));
  const [enableSponsorship, setEnableSponsorship] = useState(Boolean(device?.enable_sponsorship));
  const [enablePublic, setEnablePublic] = useState(Boolean(device?.enable_public_view));
  const [cameraEnable12V, setCameraEnable12V] = useState(Number(device?.camera_enable_12v) || 0);
  const [cameraSelect, setCameraSelect] = useState(Number(device?.camera_select) || 0);
  const [cameraLightLevel, setCameraLightLevel] = useState(Number(device?.camera_light_level) || 50);
  const isMobile = useIsMobile();
  const { canEditDeviceConfig } = useDeviceEditPermissions();
  const { role, isSystemAdmin } = useSimpleRole();
  // Power Off is restricted to org admins and superusers only — editors cannot use it.
  const canPowerOffDevice = role === 'admin' || isSystemAdmin;
  const { markPending, getFieldValue, clearExpired } = useOptimisticDeviceState();
  
  /* ==========================================================================
   * TEMPORARILY DISABLED — restore later
   * Original hibernate-based Power Off logic. Replaced by handleStopImages,
   * which clears operational days instead of hibernating the device.
   * --------------------------------------------------------------------------
  // Validation function for confirmation text
  const isValidConfirmation = (text: string): boolean => {
    const deviceName = device?.device_name || "";
    return text.toUpperCase() === "POWER OFF" || text === deviceName;
  };

  const handleConfirmPowerOff = () => {
    setShowConfirmDialog(false);
    setConfirmationText("");
    handlePowerOff();
  };

  const handlePowerOff = async () => {
    try {
      await updateField("cameraConfig.forceHibernate", "1");
      toast.success("Device hibernation initiated");
    } catch (error) {
      toast.error("Failed to initiate device hibernation");
    }
  };

  const handleCancelHibernate = async () => {
    setShowCancelHibernateDialog(false);
    try {
      await updateField("cameraConfig.forceHibernate", "0");
      toast.success("Pending power off cancelled");
    } catch (error) {
      toast.error("Failed to cancel pending power off");
    }
  };

  const getPowerButtonState = () => {
    if (forceHibernate) {
      return {
        icon: <Loader className="h-4 w-4 text-white animate-spin" strokeWidth={2.5} />,
        disabled: false,
        ariaLabel: "Cancel pending power off",
        variant: "secondary" as const,
        className: "bg-amber-500 hover:bg-amber-600 text-white",
        tooltipText: "Pending Shutdown — click to cancel",
        action: "cancel" as const
      };
    } else if (isHibernating) {
      return {
        icon: <PowerOff className="h-4 w-4 text-white" strokeWidth={2.5} />,
        disabled: true,
        ariaLabel: "Device hibernating",
        variant: "secondary" as const,
        className: "bg-gray-500 text-white",
        tooltipText: "Device Powered Off",
        action: "none" as const
      };
    } else {
      return {
        icon: <Power className="h-4 w-4 text-white" strokeWidth={2.5} />,
        disabled: false,
        ariaLabel: "Power off device",
        variant: "secondary" as const,
        className: "bg-red-500 hover:bg-red-600 text-white",
        tooltipText: "Click to Power Off",
        action: "power-off" as const
      };
    }
  };
  const powerButtonState = getPowerButtonState();
  * ======================================================================== */

  // Stop / Start Images — clears (or restores) operational days, schedule and
  // capture interval. Never hibernates the device.
  const { stopImages, startImages, isBusy: isStoppingImages } = useDeviceImageCapture();
  const imagesStopped = areImagesStopped(device?.deviceOperationalDays);
  const restoreTarget = parseSnapshot(device?.stopped_config_snapshot) ?? DEFAULT_RESTORE;
  const hasSnapshot = !!parseSnapshot(device?.stopped_config_snapshot);

  const applyLocalConfig = (intervalMs: number, start: number, end: number) => {
    markPending("cameraConfig.withinScheduleSleepTime", intervalMs);
    markPending("cameraConfig.scheduleStart", start);
    markPending("cameraConfig.scheduleEnd", end);
    setIntervalTimer(msToSlider(intervalMs));
    setUtcScheduleStart(start);
    setUtcScheduleEnd(end);
    setLocalScheduleStart(utcToLocal(start, userTimezone));
    setLocalScheduleEnd(utcToLocal(end, userTimezone));
  };

  const handleStopImages = async () => {
    setShowStopImagesDialog(false);
    const ok = await stopImages(deviceId, {
      operationalDays: device?.deviceOperationalDays,
      cameraConfig
    });
    if (ok) {
      applyLocalConfig(14400000, 8, 20);
      await onUpdate();
    }
  };

  const handleStartImages = async () => {
    setShowStartImagesDialog(false);
    const restored = await startImages(deviceId, {
      snapshot: device?.stopped_config_snapshot,
      cameraConfig
    });
    if (restored) {
      applyLocalConfig(restored.withinScheduleSleepTime, restored.scheduleStart, restored.scheduleEnd);
      await onUpdate();
    }
  };
  useEffect(() => {
    // Skip updates if device is not loaded yet
    if (!device) return;
    clearExpired();
    console.log("Device config updated or timezone changed:", device.camera_config);
    setTimezone(getFieldValue('timezone', device.timezone || "Europe/London"));

    // Process camera_config again when device updates
    let config;
    try {
      if (device.camera_config) {
        if (typeof device.camera_config === 'string') {
          try {
            config = JSON.parse(device.camera_config);
          } catch (e) {
            console.error("Failed to parse camera_config string in useEffect:", e);
            config = defaultCameraConfig;
          }
        } else if (typeof device.camera_config === 'object') {
          config = device.camera_config;
        } else {
          config = defaultCameraConfig;
        }
      } else if (device.cameraConfig) {
        config = device.cameraConfig;
      } else {
        config = defaultCameraConfig;
      }
    } catch (error) {
      console.error("Error processing camera config in useEffect:", error);
      config = defaultCameraConfig;
    }
    console.log("Processed config in useEffect:", config);
    const utcStart = Number(getFieldValue('cameraConfig.scheduleStart', config.scheduleStart || 0));
    const utcEnd = Number(getFieldValue('cameraConfig.scheduleEnd', config.scheduleEnd || 0));
    setUtcScheduleStart(utcStart);
    setUtcScheduleEnd(utcEnd);
    const tz = userTimezone;
    const localStart = utcToLocal(utcStart, tz);
    const localEnd = utcToLocal(utcEnd, tz);
    console.log(`Converting UTC to local on initial load: 
      UTC Start: ${utcStart} → Local: ${localStart} in ${tz}
      UTC End: ${utcEnd} → Local: ${localEnd} in ${tz}`);
    setLocalScheduleStart(localStart);
    setLocalScheduleEnd(localEnd);
    setCameraSaturation(mapLevelToSlider(Number(getFieldValue('cameraConfig.cameraSaturation', config.cameraSaturation) || 0)));
    setCameraEV(mapLevelToSlider(Number(getFieldValue('cameraConfig.cameraEV', config.cameraEV) || 0)));
    setCameraContrast(mapLevelToSlider(Number(getFieldValue('cameraConfig.cameraContrast', config.cameraContrast) || 0)));
    setCameraBrightness(mapLevelToSlider(Number(getFieldValue('cameraConfig.cameraBrightness', config.cameraBrightness) || 0)));
    setAbsoluteExposure(getFieldValue('cameraConfig.cameraAbsoluteExposure', config.cameraAbsoluteExposure || "0"));
    setIsoSensitivity(getFieldValue('cameraConfig.cameraISOSensitivity', config.cameraISOSensitivity || "0"));
    setIntervalTimer(msToSlider(Number(getFieldValue('cameraConfig.withinScheduleSleepTime', config.withinScheduleSleepTime) || 3600000)));
    setOnDemandPhoto(Boolean(Number(getFieldValue('cameraConfig.onDemand', config.onDemand) || 0)));
    setEnableSponsorship(Boolean(getFieldValue('enableSponsorship', device.enable_sponsorship)));
    setEnablePublic(Boolean(getFieldValue('enablePublic', device.enable_public_view)));
    setCameraEnable12V(Number(getFieldValue('cameraEnable12V', device.camera_enable_12v) || 0));
    setCameraSelect(Number(getFieldValue('cameraSelect', device.camera_select) || 0));
    setCameraLightLevel(Number(getFieldValue('cameraLightLevel', device.camera_light_level) || 50));
  }, [device]);
  const calculateMonthlyImages = () => {
    const start = utcScheduleStart;
    const end = utcScheduleEnd;
    const hoursPerDay = end > start ? end - start : 24 - start + end;
    const msPerDay = hoursPerDay * 60 * 60 * 1000;
    const imagesPerDay = Math.floor(msPerDay / sliderToMs(intervalTimer));
    return { monthlyImages: imagesPerDay * 30, imagesPerDay };
  };
  const handleUpdateField = (field: string, value: string | number) => {
    const { monthlyImages, imagesPerDay } = calculateMonthlyImages();
    const imagesAllowed = device?.contract?.imagesAllowed || 0;
    if (monthlyImages > imagesAllowed) {
      setPendingUpdate({
        field,
        value,
        imagesPerDay
      });
      setShowWarningDialog(true);
    } else {
      updateField(field, value);
    }
  };
  const handleConfirmUpdate = () => {
    if (pendingUpdate) {
      updateField(pendingUpdate.field, pendingUpdate.value);
      setPendingUpdate(null);
    }
    setShowWarningDialog(false);
  };
  const toBool = (value: number | string | undefined | null): boolean => {
    if (typeof value === 'string') {
      return value === '1';
    }
    return value === 1;
  };
  const updateField = async (field: string, value: string | number | boolean) => {
    console.log(`🔄 [DeviceConfig] Updating field: ${field} = ${value} (type: ${typeof value})`);
    
    // Mark field as pending before API call to prevent stale overwrites
    markPending(field, value);
    
    setIsUpdating(true);
    try {
      const params = new URLSearchParams();
      params.append("deviceID", deviceId);
      params.append("field", field);
      const apiValue = typeof value === 'boolean' ? (value ? 1 : 0) : value;
      params.append("value", apiValue.toString());
      
      logChange(deviceId, field, apiValue.toString());
      const response = await fetch("https://i56o7w.buildship.run/updateDevice", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded"
        },
        body: params
      });
      
      if (!response.ok) {
        throw new Error("Failed to update device");
      }
      
      console.log(`✅ [DeviceConfig] Successfully updated ${field}`);
      await onUpdate();
      toast.success("Device updated successfully");
    } catch (error) {
      console.error(`❌ [DeviceConfig] Error updating ${field}:`, error);
      toast.error("Failed to update device");
    } finally {
      setIsUpdating(false);
    }
  };
  const handleLocalScheduleStartChange = (value: number) => {
    setLocalScheduleStart(value);
    const newUtcValue = localToUTC(value, userTimezone);
    setUtcScheduleStart(newUtcValue);
    console.log(`Local start changed: ${value} local → ${newUtcValue} UTC`);
  };
  const handleLocalScheduleEndChange = (value: number) => {
    setLocalScheduleEnd(value);
    const newUtcValue = localToUTC(value, userTimezone);
    setUtcScheduleEnd(newUtcValue);
    console.log(`Local end changed: ${value} local → ${newUtcValue} UTC`);
  };
  const handleSaveScheduleStart = () => {
    console.log(`Saving schedule start: ${localScheduleStart} local (${timezone}) = ${utcScheduleStart} UTC`);
    updateField("cameraConfig.scheduleStart", utcScheduleStart);
  };
  const handleSaveScheduleEnd = () => {
    console.log(`Saving schedule end: ${localScheduleEnd} local (${timezone}) = ${utcScheduleEnd} UTC`);
    updateField("cameraConfig.scheduleEnd", utcScheduleEnd);
  };
  const handleTimezoneChange = async (value: string) => {
    // Device timezone is only used for image watermarking; schedule is untouched.
    setTimezone(value);
    await updateField("timezone", value);
  };
  const handleOnDemandToggle = async (checked: boolean) => {
    setOnDemandPhoto(checked);
    await updateField("cameraConfig.onDemand", checked ? 1 : 0);
  };

  const handleSponsorshipToggle = async (checked: boolean) => {
    setEnableSponsorship(checked);
    await updateField("enableSponsorship", checked ? 1 : 0);
  };

  const handlePublicToggle = async (checked: boolean) => {
    setEnablePublic(checked);
    await updateField("enablePublic", checked ? 1 : 0);
  };

  // If device is not loaded yet, show a loading state
  if (!device) {
    return <div className="p-4">Loading device configuration...</div>;
  }
  return <div className="p-2">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <Sliders className="h-5 w-5 text-brand-500 mr-2" />
          <h2 className="text-lg font-semibold">Device Configuration</h2>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">{imagesStopped ? "Start Images" : "Stop Images"}</span>
          <TooltipProvider>
            <Tooltip delayDuration={300}>
              <TooltipTrigger asChild>
                {/* Using a div wrapper to make sure tooltip works even when button is disabled */}
                <div className="inline-block relative" data-tour-target="power-control">
                  <Button
                    variant="secondary"
                    size="icon"
                    onClick={() => {
                      if (!canPowerOffDevice || isStoppingImages) return;
                      if (imagesStopped) setShowStartImagesDialog(true);
                      else setShowStopImagesDialog(true);
                    }}
                    disabled={!canPowerOffDevice || isStoppingImages}
                    aria-label={imagesStopped ? "Start images on this device" : "Stop all images from this device"}
                    className={`${imagesStopped ? "bg-green-600 hover:bg-green-700" : "bg-red-500 hover:bg-red-600"} text-white mr-2`}
                  >
                    {isStoppingImages
                      ? <Loader2 className="h-4 w-4 text-white animate-spin" strokeWidth={2.5} />
                      : imagesStopped
                        ? <Play className="h-4 w-4 text-white" strokeWidth={2.5} />
                        : <Power className="h-4 w-4 text-white" strokeWidth={2.5} />}
                  </Button>
                </div>
              </TooltipTrigger>
              <TooltipContent side="left">
                <p>
                  {!canPowerOffDevice
                    ? "Only administrators can change image capture"
                    : imagesStopped
                      ? "Click to restart image capture"
                      : "Click to stop all images"}
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>

      {/* Read-only banner */}
      {!canEditDeviceConfig && (
        <Alert className="mb-4">
          <Info className="h-4 w-4" />
          <AlertTitle>Read-only Mode</AlertTitle>
          <AlertDescription>
            Only administrators can modify device configuration. Contact your admin to make changes.
          </AlertDescription>
        </Alert>
      )}

      {/* Shared card components */}
      {(() => {
        const TimezoneCard = (
          <Card className="border bg-muted/50" data-tour-target="timezone-config">
            <CardHeader className="py-3">
              <div className="flex items-center gap-2">
                <Globe className="h-4 w-4 text-brand-500" />
                <CardTitle className="text-base">Timezone</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="space-y-2">
                <Label htmlFor="timezone">
                  Device Timezone
                </Label>
                <Select value={timezone} onValueChange={handleTimezoneChange} disabled={isUpdating || !canEditDeviceConfig}>
                  <SelectTrigger id="timezone" className="w-full h-8 text-xs">
                    <SelectValue placeholder="Select timezone" />
                  </SelectTrigger>
                  <SelectContent className="bg-popover">
                    {TIMEZONES.map(tz => <SelectItem key={tz.value} value={tz.value} className="text-xs">
                        {tz.label}
                      </SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        );

        const OperatingScheduleCard = (
          <Card className="border bg-muted/50" data-tour-target="operating-schedule">
            <CardHeader className="py-3">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-brand-500" />
                <CardTitle className="text-base">Operating Schedule</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="pt-0 space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="scheduleStart">Start Time (24h)</Label>
                  <div className="flex gap-1">
                    <TimeInput
                      id="scheduleStart"
                      value={localScheduleStart}
                      onChange={handleLocalScheduleStartChange}
                      min={0}
                      max={23}
                      disabled={isUpdating || !canEditDeviceConfig}
                      className="flex-1"
                    />
                    <Button variant="outline" size="sm" onClick={handleSaveScheduleStart} disabled={isUpdating || !canEditDeviceConfig} className="h-7 w-7 p-0 bg-component-background text-foreground border border-foreground">
                      <Save className="h-3 w-3" />
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">UTC: {utcScheduleStart}:00</p>
                </div>
                <div className="space-y-1">
                  <Label htmlFor="scheduleEnd">End Time (24h)</Label>
                  <div className="flex gap-1">
                    <TimeInput
                      id="scheduleEnd"
                      value={localScheduleEnd}
                      onChange={handleLocalScheduleEndChange}
                      min={0}
                      max={23}
                      disabled={isUpdating || !canEditDeviceConfig}
                      className="flex-1"
                    />
                    <Button variant="outline" size="sm" onClick={handleSaveScheduleEnd} disabled={isUpdating || !canEditDeviceConfig} className="h-7 w-7 p-0 bg-component-background text-foreground border border-foreground">
                      <Save className="h-3 w-3" />
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">UTC: {utcScheduleEnd}:00</p>
                </div>
              </div>
              <Separator />
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <Label htmlFor="intervalTimer">Capture Interval</Label>
                  <span className="text-xs font-medium">{msToDisplayValue(sliderToMs(intervalTimer))}</span>
                </div>
                <div className="flex gap-2 items-center">
                  <Slider id="intervalTimer" min={1} max={8} step={1} value={[intervalTimer]} onValueChange={value => setIntervalTimer(value[0])} className="flex-1" disabled={isUpdating || !canEditDeviceConfig} />
                  <Button variant="outline" size="sm" onClick={() => handleUpdateField("cameraConfig.withinScheduleSleepTime", sliderToMs(intervalTimer))} disabled={isUpdating || !canEditDeviceConfig} className="h-7 w-7 p-0 bg-component-background text-foreground border border-foreground">
                    <Save className="h-3 w-3" />
                  </Button>
                </div>
                <ImageCountDisplay scheduleStart={localToUTC(Number(localScheduleStart), timezone)} scheduleEnd={localToUTC(Number(localScheduleEnd), timezone)} intervalMs={sliderToMs(intervalTimer)} imagesAllowed={device.contract?.imagesAllowed || 0} />
              </div>
            </CardContent>
          </Card>
        );

        const OnDemandCard = (
          <Card className="border bg-muted/50" data-tour-target="on-demand-photos">
            <CardHeader className="py-3">
              <div className="flex items-center gap-2">
                <Camera className="h-4 w-4 text-brand-500" />
                <CardTitle className="text-base">On Demand (within Operating Hours)</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <Checkbox id="onDemandPhoto" checked={onDemandPhoto} onCheckedChange={handleOnDemandToggle} disabled={isUpdating || !canEditDeviceConfig} />
                  <Label htmlFor="onDemandPhoto" className="text-sm font-medium">
                    On Demand Photo
                  </Label>
                </div>
                {onDemandPhoto && <div className="flex items-center gap-2 p-2 bg-red-50 border border-red-200 rounded-md">
                    <AlertTriangle className="h-4 w-4 text-red-500" />
                    <span className="text-sm text-red-600">
                      Warning: This feature will result in high battery consumption.
                    </span>
                  </div>}
              </div>
            </CardContent>
          </Card>
        );

        const CameraSettingsCard = (
          <Card className="border bg-muted/50" data-tour-target="camera-settings">
            <CardHeader className="py-3">
              <div className="flex items-center gap-2">
                <Sliders className="h-4 w-4 text-brand-500" />
                <CardTitle className="text-base">Camera Settings</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              {/* Section 1: Operating Mode */}
              <h3 className="text-xs font-medium mb-3">Operating Mode</h3>
              <div className="space-y-3 mb-3">
                {/* Deactivate Images During Charging */}
                <div className="flex items-center justify-between">
                  <Label htmlFor="cameraEnable12V">Images Enabled During Charging</Label>
                  <Switch
                    id="cameraEnable12V"
                    checked={cameraEnable12V === 1}
                    onCheckedChange={(checked) => {
                      const val = checked ? 1 : 0;
                      setCameraEnable12V(val);
                      updateField("cameraConfig.cameraEnable12V", String(val));
                    }}
                    disabled={isUpdating || !canEditDeviceConfig}
                  />
                </div>

                {/* Select Camera */}
                <div className="space-y-1">
                  <Label htmlFor="cameraSelect">Select Camera</Label>
                  <Select
                    value={String(cameraSelect)}
                    onValueChange={(value) => {
                      setCameraSelect(Number(value));
                      updateField("cameraConfig.cameraSelect", value);
                    }}
                    disabled={isUpdating || !canEditDeviceConfig}
                  >
                    <SelectTrigger className="h-8 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">Auto</SelectItem>
                      <SelectItem value="1">IR Camera (Night)</SelectItem>
                      <SelectItem value="2">Colour Camera (Day)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Day/Night Threshold */}
                <div className="space-y-1">
                  <div className="flex justify-between items-center">
                    <Label htmlFor="cameraLightLevel">Day / Night Threshold</Label>
                    <span className="text-xs text-muted-foreground">{cameraLightLevel} Lux</span>
                  </div>
                  <div className="flex gap-1 items-center">
                    <Slider
                      id="cameraLightLevel"
                      min={1}
                      max={100}
                      step={1}
                      value={[cameraLightLevel]}
                      onValueChange={([val]) => setCameraLightLevel(val)}
                      disabled={isUpdating || !canEditDeviceConfig || cameraSelect !== 0}
                      className="flex-1"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => updateField("cameraConfig.cameraLightLevel", String(cameraLightLevel))}
                      disabled={isUpdating || !canEditDeviceConfig || cameraSelect !== 0}
                      className="h-7 w-7 p-0 bg-component-background text-foreground border border-foreground"
                    >
                      <Save className="h-3 w-3" />
                    </Button>
                  </div>
                  <div className="flex justify-between text-[10px] text-muted-foreground">
                    <span>1</span>
                    <span>100</span>
                  </div>
                  {cameraSelect !== 0 && (
                    <p className="text-[10px] text-muted-foreground italic">Only available when camera is set to Auto</p>
                  )}
                </div>
              </div>

              <Separator className="py-0 my-[18px]" />

              {/* Section 2: IR Camera (Night) Settings */}
              <h3 className="text-xs font-medium mb-2 py-[8px]">IR Camera (Night) Settings</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                <div className="space-y-2">
                  <Label htmlFor="cameraAbsoluteExposure">
                    Exposure Time (μs)
                  </Label>
                  <div className="flex gap-1">
                    <Input id="cameraAbsoluteExposure" type="number" min="0" max="30000" value={absoluteExposure} onChange={e => setAbsoluteExposure(e.target.value)} disabled={isUpdating || !canEditDeviceConfig} className="h-7 text-xs" />
                    <Button variant="outline" size="sm" onClick={() => updateField("cameraConfig.cameraAbsoluteExposure", absoluteExposure)} disabled={isUpdating || !canEditDeviceConfig} className="h-7 w-7 p-0 bg-component-background text-foreground border border-foreground">
                      <Save className="h-3 w-3" />
                    </Button>
                  </div>
                  <Slider
                    min={0}
                    max={30000}
                    step={100}
                    value={[Number(absoluteExposure) || 0]}
                    onValueChange={([val]) => setAbsoluteExposure(String(val))}
                    disabled={isUpdating || !canEditDeviceConfig}
                    className="py-1"
                  />
                  <div className="flex justify-between text-[10px] text-muted-foreground">
                    <span>0</span>
                    <span>30000</span>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="cameraISOSensitivity">
                    ISO Sensitivity (0-1200)
                  </Label>
                  <div className="flex gap-1">
                    <Input id="cameraISOSensitivity" type="number" min="0" max="1200" value={isoSensitivity} onChange={e => setIsoSensitivity(e.target.value)} disabled={isUpdating || !canEditDeviceConfig} className="h-7 text-xs" />
                    <Button variant="outline" size="sm" onClick={() => updateField("cameraConfig.cameraISOSensitivity", isoSensitivity)} disabled={isUpdating || !canEditDeviceConfig} className="h-7 w-7 p-0 bg-component-background text-foreground border border-foreground">
                      <Save className="h-3 w-3" />
                    </Button>
                  </div>
                  <Slider
                    min={0}
                    max={1200}
                    step={10}
                    value={[Number(isoSensitivity) || 0]}
                    onValueChange={([val]) => setIsoSensitivity(String(val))}
                    disabled={isUpdating || !canEditDeviceConfig}
                    className="py-1"
                  />
                  <div className="flex justify-between text-[10px] text-muted-foreground">
                    <span>0</span>
                    <span>1200</span>
                  </div>
                </div>
              </div>

              <Separator className="py-0 my-[18px]" />

              {/* Section 3: Colour Camera (Day) Settings */}
              <h3 className="text-xs font-medium mb-2 py-[8px]">Colour Camera (Day) Settings</h3>
              <div className="grid grid-cols-1 gap-3">
                <div className="space-y-1">
                  <div className="flex justify-between items-center">
                    <Label htmlFor="cameraSaturation">Color Saturation</Label>
                    <span className="text-xs">(Level {mapSliderToLevel(cameraSaturation)})</span>
                  </div>
                  <div className="flex gap-1 items-center">
                    <Slider id="cameraSaturation" min={1} max={6} step={1} value={[cameraSaturation]} onValueChange={value => setCameraSaturation(value[0])} className="flex-1" disabled={isUpdating || !canEditDeviceConfig} />
                    <Button variant="outline" size="sm" onClick={() => updateField("cameraConfig.cameraSaturation", mapSliderToLevel(cameraSaturation))} disabled={isUpdating || !canEditDeviceConfig} className="h-7 w-7 p-0 bg-component-background text-foreground border border-foreground">
                      <Save className="h-3 w-3" />
                    </Button>
                  </div>
                </div>

                <div className="space-y-1">
                  <div className="flex justify-between items-center">
                    <Label htmlFor="cameraEV">Exposure Value</Label>
                    <span className="text-xs">(Level {mapSliderToLevel(cameraEV)})</span>
                  </div>
                  <div className="flex gap-1 items-center">
                    <Slider id="cameraEV" min={1} max={6} step={1} value={[cameraEV]} onValueChange={value => setCameraEV(value[0])} className="flex-1" disabled={isUpdating || !canEditDeviceConfig} />
                    <Button variant="outline" size="sm" onClick={() => updateField("cameraConfig.cameraEV", mapSliderToLevel(cameraEV))} disabled={isUpdating || !canEditDeviceConfig} className="h-7 w-7 p-0 bg-component-background text-foreground border border-foreground">
                      <Save className="h-3 w-3" />
                    </Button>
                  </div>
                </div>

                <div className="space-y-1">
                  <div className="flex justify-between items-center">
                    <Label htmlFor="cameraContrast">Contrast</Label>
                    <span className="text-xs">(Level {mapSliderToLevel(cameraContrast)})</span>
                  </div>
                  <div className="flex gap-1 items-center">
                    <Slider id="cameraContrast" min={1} max={6} step={1} value={[cameraContrast]} onValueChange={value => setCameraContrast(value[0])} className="flex-1" disabled={isUpdating || !canEditDeviceConfig} />
                    <Button variant="outline" size="sm" onClick={() => updateField("cameraConfig.cameraContrast", mapSliderToLevel(cameraContrast))} disabled={isUpdating || !canEditDeviceConfig} className="h-7 w-7 p-0 bg-component-background text-foreground border border-foreground">
                      <Save className="h-3 w-3" />
                    </Button>
                  </div>
                </div>

                <div className="space-y-1">
                  <div className="flex justify-between items-center">
                    <Label htmlFor="cameraBrightness">Brightness</Label>
                    <span className="text-xs">(Level {mapSliderToLevel(cameraBrightness)})</span>
                  </div>
                  <div className="flex gap-1 items-center">
                    <Slider id="cameraBrightness" min={1} max={6} step={1} value={[cameraBrightness]} onValueChange={value => setCameraBrightness(value[0])} className="flex-1" disabled={isUpdating || !canEditDeviceConfig} />
                    <Button variant="outline" size="sm" onClick={() => updateField("cameraConfig.cameraBrightness", mapSliderToLevel(cameraBrightness))} disabled={isUpdating || !canEditDeviceConfig} className="h-7 w-7 p-0 bg-component-background text-foreground border border-foreground">
                      <Save className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        );

        const PublicSettingsCard = (
          <Card className="border bg-muted/50 mt-4" data-tour-target="public-settings">
            <CardHeader className="py-3">
              <div className="flex items-center gap-2">
                <Sliders className="h-4 w-4 text-brand-500" />
                <CardTitle className="text-base">Public Settings</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="space-y-3">
                <div className="flex items-center justify-between py-[8px]">
                  <Label htmlFor="enableSponsorship">
                    Enable Sponsorship
                  </Label>
                  <Switch 
                    id="enableSponsorship" 
                    checked={enableSponsorship} 
                    onCheckedChange={handleSponsorshipToggle} 
                    disabled={isUpdating || !canEditDeviceConfig} 
                    className="h-6 w-11"
                  />
                </div>
                <div className="flex items-center justify-between py-[8px]">
                  <Label htmlFor="enablePublic">
                    Enable Public View
                  </Label>
                  <Switch 
                    id="enablePublic" 
                    checked={enablePublic} 
                    onCheckedChange={handlePublicToggle} 
                    disabled={isUpdating || !canEditDeviceConfig} 
                    className="h-6 w-11" 
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        );

        const CellularSection = (
          <CellularNetworkSection
            cellular={device.cellular}
            cameraConfig={device.camera_config}
            onUpdateField={updateField}
            isUpdating={isUpdating}
            deviceId={deviceId}
          />
        );

        // Mobile layout - single column with less-used cards moved after cellular
        if (isMobile) {
          return (
            <div className="space-y-4">
              <OperationalDaysCard device={device} deviceId={deviceId} onUpdate={onUpdate} />
              {OperatingScheduleCard}
              <EventScheduleCard device={device} deviceId={deviceId} onUpdate={onUpdate} />
              {OnDemandCard}
              {CameraSettingsCard}
              {CellularSection}
              {TimezoneCard}
              {PublicSettingsCard}
            </div>
          );
        }

        // Desktop layout - two columns, less-used cards under cellular
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* First column */}
              <div className="space-y-4">
                <OperationalDaysCard device={device} deviceId={deviceId} onUpdate={onUpdate} />
                {OperatingScheduleCard}
                <EventScheduleCard device={device} deviceId={deviceId} onUpdate={onUpdate} />
                {OnDemandCard}
              </div>

              {/* Second column */}
              <div>
                {CameraSettingsCard}
              </div>
            </div>

            {/* Cellular Network Section spans full width */}
            {CellularSection}

            {/* Less frequently used settings - placed below cellular */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {TimezoneCard}
              {PublicSettingsCard}
            </div>
          </div>
        );
      })()}

      <AlertDialog open={showWarningDialog} onOpenChange={setShowWarningDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Warning: High Image Use</AlertDialogTitle>
            <AlertDialogDescription>
              At this interval the device may capture about {pendingUpdate?.imagesPerDay || 0} images per day, using credits faster than normal. Do you wish to continue?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
            setShowWarningDialog(false);
            setPendingUpdate(null);
          }}>
              Review settings
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleConfirmUpdate}
              className="bg-[hsl(var(--lime-button))] text-black hover:bg-[hsl(var(--lime-button))]/90"
            >
              Continue
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ====================================================================
        * TEMPORARILY DISABLED — restore later
        * Original typed-confirmation Power Off dialog and the pending
        * shutdown cancel dialog.
        * --------------------------------------------------------------------
      <AlertDialog open={showConfirmDialog} onOpenChange={(open) => {
        setShowConfirmDialog(open);
        if (!open) setConfirmationText("");
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              <AlertDialogTitle className="text-destructive">
                Power Off Device: {device?.device_name || deviceId}
              </AlertDialogTitle>
            </div>
            <AlertDialogDescription className="space-y-3">
              <p className="text-sm">
                <strong>⚠️ WARNING:</strong> This action will turn off the camera and cannot be undone remotely. 
                The only way to turn it back on is by physically pressing the wake-up button on the device.
              </p>
              <p className="text-sm">
                To confirm this action, please type either <strong>"POWER OFF"</strong> or the exact device name <strong>"{device?.device_name || deviceId}"</strong> below:
              </p>
              <div className="space-y-2">
                <Input
                  value={confirmationText}
                  onChange={(e) => setConfirmationText(e.target.value)}
                  placeholder={`Type "POWER OFF" or "${device?.device_name || deviceId}"`}
                  className={confirmationText && !isValidConfirmation(confirmationText) ? "border-destructive" : ""}
                  autoFocus
                />
                {confirmationText && !isValidConfirmation(confirmationText) && (
                  <p className="text-xs text-destructive">
                    Please type exactly "POWER OFF" or "{device?.device_name || deviceId}"
                  </p>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setConfirmationText("")}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleConfirmPowerOff}
              disabled={!isValidConfirmation(confirmationText)}
              className="bg-destructive hover:bg-destructive/90"
            >
              Confirm Power Off
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showCancelHibernateDialog} onOpenChange={setShowCancelHibernateDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Cancel pending power off for {device?.device_name || deviceId}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              The shutdown request hasn't been picked up by the camera yet. Cancelling
              will clear the pending command and the device will keep running normally.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep pending</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancelHibernate}
              className="bg-[hsl(var(--lime-button))] text-black hover:bg-[hsl(var(--lime-button))]/90"
            >
              Cancel power off
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
        * ================================================================== */}

      <AlertDialog open={showStopImagesDialog} onOpenChange={setShowStopImagesDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              <AlertDialogTitle className="text-destructive">
                Stop all images from {device?.device_name || deviceId}?
              </AlertDialogTitle>
            </div>
            <AlertDialogDescription>
              This clears every operational day for this device, so the camera will stop
              taking photos. It also sets the capture interval to 4 hours and the operating
              schedule to 08:00–20:00 UTC, so the device still wakes during the day. The
              current days, schedule and interval are saved so you can restart image
              capture with one click. The device stays powered on.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleStopImages}
              className="bg-destructive hover:bg-destructive/90"
            >
              Stop images
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={showStartImagesDialog} onOpenChange={setShowStartImagesDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <div className="flex items-center gap-2">
              <Play className="h-5 w-5 text-green-600" />
              <AlertDialogTitle>
                Restart images on {device?.device_name || deviceId}?
              </AlertDialogTitle>
            </div>
            <AlertDialogDescription>
              {hasSnapshot
                ? "This restores the settings saved when images were stopped:"
                : "No saved settings were found for this device, so these defaults will be applied:"}
              <span className="mt-2 block text-foreground">
                Days: {restoreTarget.operational_days.split(",").join(", ")}
                <br />
                Schedule: {String(restoreTarget.scheduleStart).padStart(2, "0")}:00–
                {String(restoreTarget.scheduleEnd).padStart(2, "0")}:00 UTC
                <br />
                Interval: {msToDisplayValue(restoreTarget.withinScheduleSleepTime)}
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleStartImages}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              Start images
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>;
};
export default DeviceConfig;